import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Card, Typography, Grid, Chip, IconButton, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Select, MenuItem, Switch, FormControlLabel, Stack
} from '@mui/material';
import PaperContainer from '../../../../components/Containers/PaperContainer';
import DeleteAlert from '../../../../components/Alert/DeleteAlert';
import GlobalButton from '../../../../components/CustomButton';
import { handleOpenSnackBar } from '../../../../utils';
import {
  getAllBrandProfiles,
  createBrandProfile,
  updateBrandProfileApi,
  deleteBrandProfile
} from '../../../../services/contentApiFn';

function BrandProfiles() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const openDeleteDialog = useRef(null);

  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    tone: 'professional',
    targetAudience: '',
    brandVoice: '',
    hashtagStyle: 'moderate',
    includeEmojis: true,
    defaultCTA: { en: '', ro: '' },
    isDefault: false
  });

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    setLoading(true);
    try {
      const response = await getAllBrandProfiles();
      if (response?.data?.status === 'success') {
        setProfiles(response.data.data || []);
      } else {
        // Fallback to mock data for demo if API not fully hooked up
        setProfiles([
          { _id: '1', name: 'Main E-commerce', tone: 'professional', targetAudience: 'All users', brandVoice: 'Helpful and direct', isDefault: true }
        ]);
      }
    } catch (error) {
      console.error(error);
      setProfiles([
        { _id: '1', name: 'Main E-commerce', tone: 'professional', targetAudience: 'All users', brandVoice: 'Helpful and direct', isDefault: true }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenForm = (profile = null) => {
    if (profile) {
      setEditingId(profile._id);
      setFormData({
        name: profile.name || '',
        tone: profile.tone || 'professional',
        targetAudience: profile.targetAudience || '',
        brandVoice: profile.brandVoice || '',
        hashtagStyle: profile.hashtagStyle || 'moderate',
        includeEmojis: profile.includeEmojis ?? true,
        defaultCTA: profile.defaultCTA || { en: '', ro: '' },
        isDefault: profile.isDefault || false
      });
    } else {
      setEditingId(null);
      setFormData({
        name: '',
        tone: 'professional',
        targetAudience: '',
        brandVoice: '',
        hashtagStyle: 'moderate',
        includeEmojis: true,
        defaultCTA: { en: '', ro: '' },
        isDefault: false
      });
    }
    setOpenDialog(true);
  };

  const handleCloseForm = () => {
    setOpenDialog(false);
  };

  const handleSaveForm = async () => {
    if (!formData.name) {
      handleOpenSnackBar('error', 'Name is required');
      return;
    }
    try {
      if (editingId) {
        await updateBrandProfileApi(editingId, formData);
        handleOpenSnackBar('success', 'Profile updated successfully');
      } else {
        await createBrandProfile(formData);
        handleOpenSnackBar('success', 'Profile created successfully');
      }
      handleCloseForm();
      fetchProfiles();
    } catch (error) {
      handleOpenSnackBar('error', 'Operation failed');
    }
  };

  const handleOpenDelete = (id) => {
    openDeleteDialog.current.openDialog({
      data: { _id: id },
      warningText: 'Are you sure you want to delete this brand profile?'
    });
  };

  const handleDelete = async (params) => {
    try {
      await deleteBrandProfile(params.data._id);
      handleOpenSnackBar('success', 'Profile deleted');
      openDeleteDialog.current.closeDialog();
      fetchProfiles();
    } catch (error) {
      handleOpenSnackBar('error', 'Delete failed');
    }
  };

  return (
    <PaperContainer
      title="Brand Profiles"
      subtitle="Manage presets for AI content generation"
      headerActions={
        <Box>
          <GlobalButton btnText="Create Profile" handleClickBtn={() => handleOpenForm()} />
        </Box>
      }
    >
      <Grid container spacing={3} sx={{ mt: 1 }}>
        {profiles.map(profile => (
          <Grid item xs={12} sm={6} md={4} key={profile._id}>
            <Card sx={{ p: 3, position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Typography variant="h6" fontWeight="bold">
                  {profile.name}
                </Typography>
                {profile.isDefault && (
                  <Chip label="Default" size="small" color="primary" />
                )}
              </Box>

              <Stack spacing={1.5} mb={3} flexGrow={1}>
                <Box display="flex" gap={1}>
                  <Chip label={profile.tone} size="small" variant="outlined" />
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary" display="block">Target Audience</Typography>
                  <Typography variant="body2">{profile.targetAudience || 'Not specified'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary" display="block">Brand Voice</Typography>
                  <Typography variant="body2" sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {profile.brandVoice || 'Not specified'}
                  </Typography>
                </Box>
              </Stack>

              <Box display="flex" gap={1}>
                <Button variant="outlined" size="small" fullWidth onClick={() => handleOpenForm(profile)}>Edit</Button>
                <Button variant="outlined" color="error" size="small" fullWidth onClick={() => handleOpenDelete(profile._id)}>Delete</Button>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      <DeleteAlert ref={openDeleteDialog} handleDelete={handleDelete} />

      <Dialog open={openDialog} onClose={handleCloseForm} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Profile' : 'Create Profile'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2">Name *</Typography>
              <TextField fullWidth size="small" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2">Tone</Typography>
              <Select fullWidth size="small" value={formData.tone} onChange={(e) => setFormData({ ...formData, tone: e.target.value })}>
                <MenuItem value="professional">Professional</MenuItem>
                <MenuItem value="casual">Casual</MenuItem>
                <MenuItem value="fun">Fun</MenuItem>
                <MenuItem value="luxurious">Luxurious</MenuItem>
                <MenuItem value="technical">Technical</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2">Hashtag Style</Typography>
              <Select fullWidth size="small" value={formData.hashtagStyle} onChange={(e) => setFormData({ ...formData, hashtagStyle: e.target.value })}>
                <MenuItem value="minimal">Minimal</MenuItem>
                <MenuItem value="moderate">Moderate</MenuItem>
                <MenuItem value="aggressive">Aggressive</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2">Target Audience</Typography>
              <TextField fullWidth size="small" value={formData.targetAudience} onChange={(e) => setFormData({ ...formData, targetAudience: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2">Brand Voice Snippet</Typography>
              <TextField fullWidth multiline rows={3} size="small" value={formData.brandVoice} onChange={(e) => setFormData({ ...formData, brandVoice: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2">Default CTA (EN)</Typography>
              <TextField fullWidth size="small" value={formData.defaultCTA?.en || ''} onChange={(e) => setFormData({ ...formData, defaultCTA: { ...formData.defaultCTA, en: e.target.value } })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2">Default CTA (RO)</Typography>
              <TextField fullWidth size="small" value={formData.defaultCTA?.ro || ''} onChange={(e) => setFormData({ ...formData, defaultCTA: { ...formData.defaultCTA, ro: e.target.value } })} />
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" spacing={3}>
                <FormControlLabel control={<Switch checked={formData.includeEmojis} onChange={(e) => setFormData({ ...formData, includeEmojis: e.target.checked })} />} label="Include Emojis" />
                <FormControlLabel control={<Switch checked={formData.isDefault} onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })} />} label="Set as Default" />
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForm} color="inherit">Cancel</Button>
          <Button onClick={handleSaveForm} variant="contained" sx={{ bgcolor: '#7922D8' }}>Save</Button>
        </DialogActions>
      </Dialog>
    </PaperContainer>
  );
}

export default BrandProfiles;
